import { typographyTokens, layoutTokens } from "@/shared/theme";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useRememberedPageState } from "@/features/preferences/page-state";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/app/context/AuthContext";
import { useMainScrollElement } from "@/app/context/MainScrollContext";
import { EditorialPageHeader, EvidenceLabel } from "@/shared/components";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { EvidenceExplorer, useExplorerCompact } from "@/shared/components/evidence-explorer";
import { trackProductEvent } from "@/shared/analytics/ga4";
import { PerformanceSelection, usePerformanceSelection } from "@/features/performance-selection";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { type FocusCohort, type DevelopmentArea, type PatternStat } from "./patterns.api";
import { usePatterns } from "./usePatterns";
import { MicroLabel, TrackedPatterns } from "./components";
import { parsePatternNavigation, patternIdFrom, withPatternId } from "./pattern-navigation";
import { buildPatternExplorer, resolvePatternSelection } from "./pattern-explorer";
import { buildSummaryTiles } from "./pattern-summary";
import { patternEvidenceLabels, patternExamplePly } from "./pattern-dossier";
import { PatternExplorerRow, explorerItemTitle } from "./explorer/PatternExplorerRow";
import { PatternExplorerPanel } from "./explorer/PatternExplorerPanel";
import { SkillChips } from "./explorer/SkillChips";
import { SummaryStrip } from "./explorer/SummaryStrip";

const PATTERN_QUALITY_GAMES = 10;

const PatternWorkspaceHeader = ({ cohort }: { cohort: FocusCohort }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      flexWrap: "wrap",
      mb: 2.5,
      pb: 1.25,
      borderBottom: layoutTokens.dividerBorder,
      borderColor: "divider",
    }}
  >
    <Box>
      <MicroLabel>STEP 02 / {cohort.toUpperCase()}</MicroLabel>
      <Typography sx={{ mt: 0.25, fontSize: "caption.fontSize", color: "text.secondary" }}>
        Evidence and Focus from this format only.
      </Typography>
    </Box>
    <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
      CHANGE SCOPE ABOVE
    </Typography>
  </Box>
);

interface PatternWorkspaceProps {
  focusCohort: FocusCohort;
  exactTimeControl?: string;
  navigation: ReturnType<typeof parsePatternNavigation>;
  embedded?: boolean;
}

const PatternWorkspace = ({ focusCohort, exactTimeControl, navigation, embedded = false }: PatternWorkspaceProps) => {
  const { profile } = useAuth();
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const patternsAllowed = Boolean(
    entitlementSnapshot &&
    (entitlementSnapshot.coreOpenBeta === true ||
      entitlementSnapshot.membership.active ||
      entitlementSnapshot.trial.patternsUnlocked),
  );
  const { stats, isLoading, error } = usePatterns(focusCohort, exactTimeControl, patternsAllowed);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const compact = useExplorerCompact();
  const scrollContainer = useMainScrollElement();
  const [rememberedId, setRememberedId] = useRememberedPageState<string | null>(
    `patterns:${focusCohort}:${exactTimeControl ?? "all"}:motif`,
    navigation.motif,
  );
  const [selectedArea, setSelectedArea] = useRememberedPageState<DevelopmentArea["key"] | null>(
    `patterns:${focusCohort}:${exactTimeControl ?? "all"}:area`,
    null,
  );
  const urlId = patternIdFrom(searchParams);
  // Set only when this page pushed the phone detail view, so "All patterns" can pop it with Back.
  const pushedDetail = useRef(false);
  /** Wide screens replace the URL so Back leaves the page; phones push so Back returns to the list. */
  const select = (id: string | null) => {
    if (!compact) setRememberedId(id);
    // Only entering the detail from the list adds a history step; switching inside the detail replaces it.
    const push = compact && id !== null && urlId === null;
    if (push) pushedDetail.current = true;
    setSearchParams((current) => withPatternId(current, id), { replace: !push, preventScrollReset: true });
  };
  useEffect(() => {
    // Any return to the list (button, browser, or OS Back) ends the pushed detail.
    if (urlId === null) pushedDetail.current = false;
  }, [urlId]);
  /** Brings the explorer on screen when a pick came from above it (tracked cards, summary tiles). */
  const reveal = (id: string) => {
    select(id);
    if (compact) return;
    requestAnimationFrame(() =>
      document.querySelector("[data-evidence-explorer]")?.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      }),
    );
  };
  const back = () => {
    if (pushedDetail.current) {
      pushedDetail.current = false;
      window.history.back();
    } else select(null);
  };

  useEffect(() => {
    if (stats) trackProductEvent("patterns_viewed");
  }, [stats]);
  if (profile === null && !isLoading) {
    return (
      <Box
        sx={{
          minHeight: layoutTokens.screenMinHeight,
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          py: layoutTokens.workspacePagePadding,
        }}
      >
        <Typography variant="h6" sx={{ color: "primary.main", mb: 1 }}>
          Profile not linked
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Link your Chess.com account on your{" "}
          <RouterLink to="/profile" style={{ color: "primary.main", textDecoration: "underline" }}>
            profile
          </RouterLink>{" "}
          to build a pattern scan.
        </Typography>
      </Box>
    );
  }

  if (!entitlementSnapshot || isLoading) {
    return <ChesslaLoadingMark fillContent={!embedded} page="Patterns" label="Nikola is scanning your patterns" />;
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: layoutTokens.screenMinHeight,
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          py: layoutTokens.workspacePagePadding,
        }}
      >
        <Typography sx={{ color: "secondary.main" }}>{error}</Typography>
      </Box>
    );
  }

  const analyzedGames = patternsAllowed
    ? (stats?.analyzedGameCount ?? 0)
    : entitlementSnapshot.trial.selectedGames.filter((game) => game.locked).length;
  const remainingGames = Math.max(0, PATTERN_QUALITY_GAMES - analyzedGames);
  const onboardingLocked = !patternsAllowed && !entitlementSnapshot.onboarding.complete;
  const clubLocked = !patternsAllowed && entitlementSnapshot.onboarding.complete && !entitlementSnapshot.trial.eligible;
  if (!patternsAllowed || !stats || analyzedGames < PATTERN_QUALITY_GAMES) {
    return (
      <Box
        sx={{
          minHeight: layoutTokens.screenMinHeight,
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          py: layoutTokens.workspacePagePadding,
        }}
      >
        <Box sx={{ maxWidth: layoutTokens.readingMaxWidth }}>
          <PatternWorkspaceHeader cohort={focusCohort} />
          <MicroLabel>♜ Pattern scan</MicroLabel>
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: "h4.fontSize", md: "h3.fontSize" },
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
            }}
          >
            {onboardingLocked
              ? "Finish your first review to start finding repeat moves."
              : clubLocked
                ? "Join Nikola's Club to unlock Patterns."
                : "Give Nikola ten games to find your repeat moves."}
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              maxWidth: layoutTokens.detailMaxWidth,
              color: "text.secondary",
              lineHeight: typographyTokens.lineHeight.body,
            }}
          >
            {clubLocked
              ? "Club keeps your analyzed library available for format-specific pattern scans."
              : "Patterns need repetition. Nikola reviews this format separately, so a Daily habit never gets mixed with Bullet, Blitz, or Rapid."}
          </Typography>
          {!clubLocked && (
            <Box
              sx={{
                bgcolor: "background.default",
                border: layoutTokens.dividerBorder,
                borderColor: "divider",
                mt: 3,
                p: 2.5,
              }}
            >
              <Box sx={{ alignItems: "baseline", display: "flex", justifyContent: "space-between" }}>
                <MicroLabel>Games reviewed</MicroLabel>
                <Typography sx={{ color: "primary.main", fontWeight: "fontWeightBold" }}>
                  {analyzedGames} / {PATTERN_QUALITY_GAMES}
                </Typography>
              </Box>
              <Box
                aria-label={`${analyzedGames} of ${PATTERN_QUALITY_GAMES} games reviewed`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={PATTERN_QUALITY_GAMES}
                aria-valuenow={analyzedGames}
                sx={{ bgcolor: "divider", height: layoutTokens.evidenceRailHeight, mt: 1 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    height: "100%",
                    width: `${(analyzedGames / PATTERN_QUALITY_GAMES) * 100}%`,
                  }}
                />
              </Box>
              <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", mt: 1 }}>
                Analyze {remainingGames} more game{remainingGames === 1 ? "" : "s"} to unlock the scan.
              </Typography>
            </Box>
          )}
          <Button
            component={RouterLink}
            to={clubLocked ? "/subscription" : "/analyze"}
            variant="contained"
            sx={{ mt: 2.5 }}
          >
            {clubLocked ? "Join Nikola's Club" : onboardingLocked ? "Finish onboarding" : "Analyze more games"}
          </Button>
        </Box>
      </Box>
    );
  }

  const developmentAreas = stats.developmentAreas ?? [];
  const explorer = buildPatternExplorer(stats, selectedArea);
  const selectedId = resolvePatternSelection(explorer, compact ? urlId : (urlId ?? rememberedId), compact);
  const explorerIds = new Set(explorer.groups.flatMap((group) => group.items.map((item) => item.id)));
  const tacticalItems = explorer.groups.find((group) => group.id === "tactical")?.items ?? [];
  const dossierItem =
    tacticalItems.find((item) => item.id === selectedId) ??
    tacticalItems.find((item) => item.id === explorer.defaultId) ??
    tacticalItems[0];
  const dossierPattern = dossierItem?.kind === "tactical" ? dossierItem.pattern : null;
  const reviewExample = (example: PatternStat["examples"][number]) => {
    const ply = patternExamplePly(example.ply);
    void navigate(
      ply == null
        ? `/games/${example.gameId}`
        : `/games/${example.gameId}?ply=${ply}&move=${example.moveNumber}&san=${encodeURIComponent(example.san)}`,
    );
  };
  const reviewCanonicalDrill = () => {
    const drill = stats.drills[0];
    if (drill) reviewExample(drill);
  };
  const dossierLabels = dossierPattern
    ? patternEvidenceLabels({
        cohort: focusCohort,
        analyzedGames,
        occurrences: dossierPattern.count,
        affectedGames: dossierPattern.gameCount,
        distinctDates: dossierPattern.distinctDates ?? 0,
        maxGames: stats.evidenceWindow?.maxGames ?? 50,
        maxAgeDays: stats.evidenceWindow?.maxAgeDays ?? 180,
      })
    : [];

  return (
    <Box
      sx={{
        minHeight: "100%",
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        py: layoutTokens.workspacePagePadding,
      }}
    >
      <Box sx={{ pb: 5 }}>
        <Box
          sx={{
            "& h1": {
              fontFamily: "fontFamily",
              fontSize: "h3.fontSize",
              letterSpacing: typographyTokens.tracking.heading,
              lineHeight: typographyTokens.lineHeight.heading,
              maxWidth: "none",
              overflowWrap: "normal",
            },
          }}
        >
          <EditorialPageHeader
            kicker={`STEP 02 / ${focusCohort.toUpperCase()}`}
            title="Know what to work on."
            metadata={dossierLabels.map(({ kind, text }) => (
              <EvidenceLabel key={kind} kind={kind}>
                {text}
              </EvidenceLabel>
            ))}
            action={
              <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
                CHANGE SCOPE ABOVE
              </Typography>
            }
          />
        </Box>
        <TrackedPatterns value={stats.focus} selectedKey={selectedId} selectableKeys={explorerIds} onSelect={reveal} />
        <SummaryStrip tiles={buildSummaryTiles(stats, explorerIds)} onOpenItem={reveal} />
        <Box sx={{ mt: layoutTokens.sectionGap }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              mb: 1,
            }}
          >
            <MicroLabel>Explore the evidence</MicroLabel>
            <Button
              variant="contained"
              size="small"
              disabled={stats.drills.length === 0}
              onClick={reviewCanonicalDrill}
            >
              {stats.drills.length > 0 ? "Start drill" : "No drills yet"}
            </Button>
          </Box>
          <EvidenceExplorer
            label={`${focusCohort} patterns`}
            groups={explorer.groups}
            selectedId={selectedId}
            compact={compact}
            backLabel="All patterns"
            scrollContainer={scrollContainer}
            toolbar={<SkillChips areas={developmentAreas} selected={selectedArea} onSelect={setSelectedArea} />}
            emptyPanel={
              <Box sx={{ p: 3, border: layoutTokens.dividerBorder, borderColor: "divider" }}>
                <MicroLabel>No verified tactical repeat yet</MicroLabel>
                <Typography sx={{ mt: 0.75, fontWeight: "fontWeightBold" }}>
                  Nikola has not verified a repeating tactical motif for this format.
                </Typography>
              </Box>
            }
            onSelect={select}
            onBack={back}
            itemLabel={(item) => `Show ${explorerItemTitle(item)} evidence`}
            renderRow={(item) => (
              <PatternExplorerRow
                item={item}
                analyzedGames={analyzedGames}
                weeks={
                  item.kind === "tactical"
                    ? stats.focus?.progress?.find((entry) => entry.key === item.id)?.weeks
                    : undefined
                }
              />
            )}
            renderPanel={(item) => (
              <PatternExplorerPanel
                item={item}
                format={focusCohort}
                exactTimeControl={exactTimeControl}
                analyzedGames={analyzedGames}
                onReview={reviewExample}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const PatternsPage = () => {
  const [searchParams] = useSearchParams();
  const navigation = parsePatternNavigation(searchParams);
  const performance = usePerformanceSelection(navigation.cohort, { page: "patterns" });

  if (performance.loading)
    return <ChesslaLoadingMark fillContent page="Patterns" label="Reading your analyzed game library" />;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          maxWidth: layoutTokens.workspaceMaxWidth,
          mx: "auto",
          px: layoutTokens.workspacePagePadding,
          pt: { xs: 2, md: 3 },
        }}
      >
        {performance.error ? (
          <Typography role="alert" sx={{ color: "secondary.main" }}>
            {performance.error}
          </Typography>
        ) : performance.empty ? (
          <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Typography sx={{ fontWeight: "fontWeightBold" }}>Analyze a game to unlock Patterns.</Typography>
            <Button component={RouterLink} to="/analyze" variant="contained" sx={{ mt: 2 }}>
              Analyze a game
            </Button>
          </Paper>
        ) : (
          performance.selection && (
            <PerformanceSelection
              tourPage="patterns"
              selection={performance.selection}
              formats={performance.formats}
              clocks={performance.clocks}
              resultsLabel="Patterns"
              onEdit={performance.edit}
              onFormat={performance.selectFormat}
              onClock={performance.selectClock}
            >
              {performance.showResults &&
                performance.selectedFormats.map((format) => (
                  <Box component="section" key={format} aria-label={`${format} patterns`}>
                    <PatternWorkspace
                      focusCohort={format}
                      exactTimeControl={
                        performance.query?.format === format ? performance.query.exactTimeControl : undefined
                      }
                      navigation={
                        navigation.cohort === format ? navigation : { step: "format", cohort: null, motif: null }
                      }
                      embedded={performance.selectedFormats.length > 1}
                    />
                  </Box>
                ))}
            </PerformanceSelection>
          )
        )}
      </Box>
    </Box>
  );
};
